/**
 * 현장 접근 판정 — **행 렌더 루프에서도 쓸 수 있는 순수 함수**.
 *
 * ## `usePermissions().isSiteAssigned`와 이름이 같지만 **다른 질문**이다
 *
 * | | 묻는 것 | owner |
 * |---|---|---|
 * | 여기 `isSiteAssigned` | 이 현장에 **닿을 수 있나** | ⇒ 무조건 true(소유자는 전 현장) |
 * | `usePermissions().isSiteAssigned`(`hooks/usePermissions.ts`) | 이 현장에 **배정되어 있나** | 특례 없음 |
 *
 * 훅 쪽은 `isOrgOwner || (isSiteAssigned && …)` 형태로 소유자 항을 **밖에서** 얹는 소비처들이
 * 쓴다(`useInspectionDetailPage.ts:1247` 등). 그래서 둘을 「중복」으로 보고 합치면 그 식들이
 * 조용히 달라진다 — **합치지 말 것.** 대신 겹치는 조각(배정 행 조회)만 아래 `hasSiteAssignment`로
 * 한 곳에 두고, 소유자 층은 이 파일이 얹는다.
 *
 * ## 왜 훅 하나로 수렴하지 못하나
 *
 * 호출부 다수가 **행마다 `siteId`가 다른 목록 렌더 루프 안**이다(`InspectionTable`·
 * `InspectionListFold`·`InspectionListPhone`·`pages/reports/index.tsx`). 거기서는 훅을 부를 수
 * 없으므로 순수 함수가 필요하다.
 */

/** 이 판정에 필요한 멤버십 조각만. 옛 `role`은 **읽지 않는다**(아래 `isSiteAssigned` doc). */
export interface SiteAccessMembership {
  /**
   * 소유자 **성질** — `/auth/me`의 `currentMembership.isOwner`(서버 `resolvePermissions().isOwner`).
   * 옛 `role` 컬럼과 독립이다(소유자 역할 «배정»에서 온다).
   */
  isOwner?: boolean;
  siteAssignments?: { siteId: string }[];
}

/**
 * 이 현장에 **배정 행이 있나** — 소유자 특례 없음.
 *
 * `usePermissions`가 자기 `isSiteAssigned`를 이 함수로 만든다. 규칙 조각을 한 곳에 두되
 * 소유자 층은 얹지 않는다(위 표).
 */
export function hasSiteAssignment(
  membership: SiteAccessMembership | null | undefined,
  siteId: string | undefined,
): boolean {
  if (!membership || !siteId) return false;
  return membership.siteAssignments?.some((a) => a.siteId === siteId) ?? false;
}

/**
 * 이 현장에 닿을 수 있나 — 소유자면 전 현장.
 *
 * ## 소유자 성질을 여기서 읽는 것이 왜 정당한가
 *
 * 조직 RBAC(P4~P6)의 규약은 「**행위** 판정에 `isOwner`를 쓰지 말 것 — 소유자는 서버가 전체
 * 키를 주므로 `can(key)`가 이미 답한다」이다(`hooks/usePermissions.ts` doc). 여기서 묻는 것은
 * 행위가 아니라 **「이 현장이 내 관할인가」**라는 조직 구조의 질문이고, 소유자가 전 현장을
 * 갖는 것은 키로 표현되지 않는 성질이다(서버도 `getAccessibleSiteIds`에서 똑같이 성질로 answer).
 * 그래서 게이트를 `배정 현장 && can(key)` 꼴로 쓸 때 **소유자는 현장 항에서 통과**하고 키 항은
 * 규약대로 `can(key)`가 답한다 — 두 규칙이 충돌하지 않는다.
 *
 * ⚠️ 소유자 판정은 `isOwner` **필드만** 본다. 옛 `role === 'OWNER'` 비교는 하지 않는다
 * (`lib/auth.ts`의 `isOwner` doc: 「P4부터 `usePermissions().isOwner`의 유일한 원천, 옛
 * `role === 'OWNER'` 폴백 없음」). 서버도 같은 축이다 —
 * `sites/site-access.service.ts`의 `getAccessibleSiteIds`가 `resolvePermissions().isOwner`로
 * 전 현장 접근을 판정하며 「옛 `role === 'OWNER'` 비교는 하지 않는다(P4 ②)」라 주석을 달고 있다.
 *
 * 두 값은 **독립**이라 양방향으로 갈린다: 커스텀 소유자 역할(`isOwner: true`인데 `role`이
 * 'OWNER'가 아님)은 옛 축에서 전 현장을 잃고, 반대로 옛 컬럼만 'OWNER'인 멤버(소유자 역할
 * 배정 없음)는 옛 축에서 전 현장을 **부당하게 얻는다**. 뒤쪽이 서버와 갈리던 자리다.
 */
export function isSiteAssigned(
  membership: SiteAccessMembership | null,
  siteId: string,
): boolean {
  if (!membership) return false;
  if (membership.isOwner === true) return true;
  return hasSiteAssignment(membership, siteId);
}
